import { ColumnDef } from '@tanstack/react-table';
import { toast } from 'react-toastify';
import { Trash2, Edit } from 'lucide-react';
import { GpsIntegrationsQueryData } from '@area-restrita/api-client/model/gpsIntegrationsQueryData';
import { useDeleteGpsIntegration } from '@area-restrita/api-client/endpoints/gps-integrations/gps-integrations.gen';

import Dialog from '@/components/template/Dialog';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { useAuthSessionStore } from '@/modules/authentication/stores/authSessionStore';

import EditGpsIntegrationModal from './components/EditGpsIntegrationModal';
import { getProviderLabel } from './components/gpsProviderConfigs';

export const columns: ColumnDef<GpsIntegrationsQueryData>[] = [
  {
    accessorKey: 'id',
    header: 'ID',
    cell: ({ row }) => <span className="font-mono">#{row.original.id}</span>,
  },
  {
    accessorKey: 'gpsProvider',
    header: 'Provedor GPS',
    cell: ({ row }) => getProviderLabel(row.original.gpsProvider),
  },
  {
    accessorKey: 'priority',
    header: 'Prioridade',
    cell: ({ row }) => (
      <span className="font-medium">{row.original.priority}</span>
    ),
  },
  {
    accessorKey: 'updatedAt',
    header: 'Atualizado em',
    cell: ({ row }) => {
      const date = new Date(row.original.updatedAt);
      return (
        <span className="text-muted-foreground">
          {date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      );
    },
    meta: { className: 'text-right' },
  },
  {
    header: 'Ações',
    meta: { className: 'text-right', onClick: (e) => e.stopPropagation() },
    cell: function GpsIntegrationActionsCell({ row }) {
      const { checkAuthorization } = useAuthSessionStore();

      const canDelete = checkAuthorization(
        ({ can }) => can('delete', 'departments'), // TODO: Atualizar quando a permissão gps_integrations estiver disponível
      );
      const canEdit = checkAuthorization(
        ({ can }) => can('update', 'departments'), // TODO: Atualizar quando a permissão gps_integrations estiver disponível
      );

      const { mutateAsync: deleteGpsIntegration } = useDeleteGpsIntegration();

      return (
        <div className="flex justify-end gap-3">
          <EditGpsIntegrationModal
            integration={row.original}
            trigger={
              <Button variant="outline" size="icon-sm" disabled={!canEdit}>
                <Edit className="h-4 w-4" />
              </Button>
            }
          />
          <Dialog
            title="Excluir Integração GPS"
            description="Esta ação não pode ser desfeita. A integração GPS será removida permanentemente."
            trigger={
              <Button
                variant="destructive"
                size="icon-sm"
                disabled={!canDelete}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            }
          >
            {({ handleClose }) => (
              <>
                <p>
                  Tem certeza que deseja excluir a integração GPS{' '}
                  <strong>#{row.original.id}</strong> (
                  {getProviderLabel(row.original.gpsProvider)})?
                </p>
                <DialogFooter>
                  <Button variant="outline" onClick={handleClose}>
                    Cancelar
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={async () => {
                      await deleteGpsIntegration(
                        { id: row.original.id },
                        {
                          onSuccess: () => {
                            toast.success(
                              'Integração GPS excluída com sucesso!',
                            );
                          },
                          onError: () =>
                            toast.error('Erro ao excluir integração GPS!'),
                        },
                      );
                      handleClose();
                    }}
                  >
                    Excluir
                  </Button>
                </DialogFooter>
              </>
            )}
          </Dialog>
        </div>
      );
    },
  },
];
